import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Calendar,
  CheckSquare,
  XSquare,
  AlertTriangle,
  Target,
  TrendingUp,
  Users,
  Building2,
  Clock,
  Star,
  Lightbulb,
  ChevronRight
} from 'lucide-react';

const WeeklySummaryForm = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weeklyPlans, setWeeklyPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [incompleteTasks, setIncompleteTasks] = useState([]);
  const [taskExplanations, setTaskExplanations] = useState({});
  const [summary, setSummary] = useState({
    achievements: '',
    challenges: '',
    improvements: '',
    nextWeekPlanning: ''
  });

  // טעינת תכנונים שבועיים - רק פעם אחת או כשהיוזר משתנה
  useEffect(() => {
    if (currentUser?.id) {
      const plans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      const userPlans = plans.filter(plan => plan.userId === currentUser.id);
      setWeeklyPlans(userPlans);
    }
  }, [currentUser?.id]);

  // טעינת תכנון שבועי נבחר - משתמש ב-useMemo כדי למנוע חישובים מיותרים
  const currentPlan = useMemo(() => {
    if (!selectedWeek || !weeklyPlans) return null;
    return weeklyPlans.find(p => p.summaryData?.weekStart === selectedWeek);
  }, [selectedWeek, weeklyPlans]);

  // עדכון המשימות כשהתכנון משתנה
  useEffect(() => {
    if (selectedWeek) {
      if (currentPlan) {
        setSelectedPlan(currentPlan);
        
        // טעינת משימות מהתכנון השבועי
        const allTasks = [];
        Object.values(currentPlan.schedule || {}).forEach(daySchedule => {
          Object.values(daySchedule).forEach(timeSlotTasks => {
            if (Array.isArray(timeSlotTasks)) {
              allTasks.push(...timeSlotTasks);
            }
          });
        });
        
        setCompletedTasks([]);
        setIncompleteTasks([...allTasks]);
      } else {
        // אם אין תכנון קיים לשבוע זה, ניצור תכנון ריק
        setSelectedPlan({
          summaryData: { weekStart: selectedWeek },
          schedule: {},
          selectedTasks: []
        });
        setCompletedTasks([]);
        setIncompleteTasks([]);
      }
    }
  }, [selectedWeek, currentPlan]); // שימוש ב-currentPlan במקום weeklyPlans

  const handleTaskStatusChange = (task, isCompleted) => {
    if (isCompleted) {
      setCompletedTasks(prev => [...prev, task]);
      setIncompleteTasks(prev => prev.filter(t => t.id !== task.id));
    } else {
      setIncompleteTasks(prev => [...prev, task]);
      setCompletedTasks(prev => prev.filter(t => t.id !== task.id));
    }
  };

  const handleSave = () => {
    const weeklySummary = {
      id: Date.now().toString(),
      userId: currentUser?.id || currentUser?.uid,
      userName: currentUser?.name,
      userRole: currentUser?.role,
      departmentId: currentUser?.departmentId,
      departmentName: currentUser?.departmentName,
      teamId: currentUser?.teamId,
      teamName: currentUser?.teamName,
      weekStart: selectedWeek,
      completedTasks,
      incompleteTasks,
      taskExplanations,
      summary,
      type: 'weekly-summary',
      category: 'summary',
      createdAt: new Date().toISOString()
    };
  
    const summaries = JSON.parse(localStorage.getItem('weeklySummaries') || '[]');
    
    const existingIndex = summaries.findIndex(s => 
      s.weekStart === selectedWeek && s.userId === currentUser?.id
    );
    
    if (existingIndex >= 0) {
      summaries[existingIndex] = weeklySummary;
    } else {
      summaries.push(weeklySummary);
    }
    
    localStorage.setItem('weeklySummaries', JSON.stringify(summaries));
    alert('הסיכום השבועי נשמר בהצלחה!');
    navigate('/dashboard');
  };

  const handlePlanNextWeek = () => {
    // מעבר לתכנון השבוע הבא
    const nextWeekStart = getNextWeekStart();
    
    // שמירת הסיכום הנוכחי לפני המעבר
    handleSave();
    
    // מעבר לטופס התכנון עם השבוע הנבחר
    const event = new CustomEvent('openDailyPlanner', {
      detail: {
        selectedWeek: nextWeekStart,
        planningType: 'weekly'
      }
    });
    window.dispatchEvent(event);
  };

  const getNextWeekStart = () => {
    const nextWeek = new Date(selectedWeek);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek.toISOString().split('T')[0];
  };

  const getWeekDates = (weekStart) => {
    const start = new Date(weekStart);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return days[date.getDay()];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">סיכום שבוע</h1>
                <p className="text-gray-600">סכם את השבוע שעבר ותכנן את השבוע הבא</p>
              </div>
            </div>
          </div>
        </div>

        {/* בחירת שבוע */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Calendar className="w-6 h-6 text-blue-500" />
            <span>בחר שבוע לסיכום</span>
          </h2>
          
          {/* בחירת שבוע חופשית */}
          <div className="mb-6">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                בחר שבוע לסיכום:
              </label>
              <input
                type="date"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                max={new Date().toISOString().split('T')[0]}
              />
              {selectedWeek && (
                <div className="mt-2 text-sm text-gray-600">
                  נבחר שבוע: {getWeekDates(selectedWeek).map(date => 
                    getDayName(date)
                  ).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* תכנונים קיימים */}
          {weeklyPlans.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">או בחר מתכנון קיים:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {weeklyPlans.map((plan, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedWeek(plan.summaryData?.weekStart)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      selectedWeek === plan.summaryData?.weekStart
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        שבוע {plan.summaryData?.weekStart}
                      </div>
                      <div className="text-sm text-gray-600">
                        {plan.summaryData?.weekStart && getWeekDates(plan.summaryData.weekStart).map(date => 
                          getDayName(date)
                        ).join(', ')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {selectedWeek && selectedPlan && (
          <>
            {/* סטטיסטיקות */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckSquare className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{completedTasks.length}</div>
                    <div className="text-sm text-gray-600">משימות הושלמו</div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <XSquare className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{incompleteTasks.length}</div>
                    <div className="text-sm text-gray-600">משימות לא הושלמו</div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {completedTasks.length + incompleteTasks.length > 0 
                        ? Math.round((completedTasks.length / (completedTasks.length + incompleteTasks.length)) * 100)
                        : 0}%
                    </div>
                    <div className="text-sm text-gray-600">אחוז הצלחה</div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{completedTasks.length + incompleteTasks.length}</div>
                    <div className="text-sm text-gray-600">סה"כ משימות</div>
                  </div>
                </div>
              </div>
            </div>

            {/* משימות */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* משימות שהושלמו */}
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <CheckSquare className="w-5 h-5 text-green-500" />
                  <span>משימות שהושלמו</span>
                </h3>
                <div className="space-y-3">
                  {completedTasks.map((task, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                      <button
                        onClick={() => handleTaskStatusChange(task, false)}
                        className="w-6 h-6 bg-green-500 rounded flex items-center justify-center text-white hover:bg-green-600 transition-colors"
                      >
                        ✓
                      </button>
                      <div className="flex-1 text-right">
                        <div className="font-medium text-gray-900">{task.title}</div>
                        <div className="text-sm text-gray-600">{task.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* משימות שלא הושלמו */}
              <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <XSquare className="w-5 h-5 text-red-500" />
                  <span>משימות שלא הושלמו</span>
                </h3>
                <div className="space-y-4">
                  {incompleteTasks.map((task, index) => (
                    <div key={index} className="p-4 bg-red-50 rounded-xl border border-red-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <button
                          onClick={() => handleTaskStatusChange(task, true)}
                          className="w-6 h-6 bg-gray-300 rounded flex items-center justify-center text-gray-600 hover:bg-green-500 hover:text-white transition-colors"
                        >
                          +
                        </button>
                        <div className="flex-1 text-right">
                          <div className="font-medium text-gray-900">{task.title}</div>
                          <div className="text-sm text-gray-600">{task.description}</div>
                        </div>
                      </div>
                      <div className="mr-9">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          למה לא הושלמה המשימה?
                        </label>
                        <textarea
                          value={taskExplanations[task.id] || ''}
                          onChange={(e) => setTaskExplanations(prev => ({
                            ...prev,
                            [task.id]: e.target.value
                          }))}
                          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                          rows="2"
                          placeholder="הסבר למה המשימה לא הושלמה..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* סיכום טקסטואלי */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span>סיכום השבוע</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    הישגים והצלחות
                  </label>
                  <textarea
                    value={summary.achievements}
                    onChange={(e) => setSummary(prev => ({ ...prev, achievements: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="מה השגתי השבוע? מה הצליח לי?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    אתגרים וקשיים
                  </label>
                  <textarea
                    value={summary.challenges}
                    onChange={(e) => setSummary(prev => ({ ...prev, challenges: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="מה היו האתגרים? מה הקשה עלי?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    שיפורים ושימור
                  </label>
                  <textarea
                    value={summary.improvements}
                    onChange={(e) => setSummary(prev => ({ ...prev, improvements: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="מה אני רוצה לשפר? מה לשמר?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    תכנון השבוע הבא
                  </label>
                  <textarea
                    value={summary.nextWeekPlanning}
                    onChange={(e) => setSummary(prev => ({ ...prev, nextWeekPlanning: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="מה אני רוצה להשיג השבוע הבא?"
                  />
                </div>
              </div>
            </div>

            {/* כפתורי פעולה */}
            <div className="flex justify-center space-x-4 mt-8">
              <button
                onClick={handlePlanNextWeek}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3 text-lg font-semibold"
              >
                <Target className="w-6 h-6" />
                <span>תכנן שבוע הבא</span>
              </button>
              <button
                onClick={handleSave}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl hover:from-green-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3 text-lg font-semibold"
              >
                <Save className="w-6 h-6" />
                <span>שמור סיכום</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WeeklySummaryForm;